import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { getByHandle, getRelated } from '@/data/loader';
import { getPostsMetaByHub } from '@/content/blog/loader';
import { useI18n, useLocalizedPath } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useGooglePhotos } from '@/hooks/useGooglePhotos';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { EntityDetailSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { NotFoundPage } from './NotFoundPage';
import { EntityGallery } from '@/components/EntityGallery';
import { EntityWidgets } from '@/components/EntityWidgets';
import {
  PraiaQuickFacts,
  PraiaTideSplit,
  PraiaArrivalTimeline,
  PraiaPackingGrid,
  PraiaTideCTA,
  PraiaComparativa,
} from '@/components/PraiaWidgets';
import { RichText } from '@/components/RichText';
import { PousadaProvaSocial, PousadaSuperSections } from '@/components/PousadaWidgets';
import { RestauranteProvaSocial, RestauranteSuperSections } from '@/components/RestauranteWidgets';
import { AtracaoVisualSections, NavegacaoPilar } from '@/components/AtracaoWidgets';
import { BarVisualSections } from '@/components/BarWidgets';
import { AuthorBox } from '@/components/AuthorBox';
import { defaultAuthor } from '@/data/author';
import googleThumbs from '../../public/images/google/thumbs.json';

// CTA "Quer explorar mais?" — categorias que têm página pilar (to + âncora do botão)
const PILAR_NAV: Record<string, { to: string; label: string }> = {
  pousada: { to: '/pousadas-ilha-do-mel', label: 'todas as pousadas' },
  praia: { to: '/praias-ilha-do-mel', label: 'todas as praias' },
  restaurante: { to: '/restaurantes-ilha-do-mel', label: 'todos os restaurantes' },
  atracao: { to: '/pontos-turisticos-ilha-do-mel', label: 'todos os pontos turísticos' },
  trilha: { to: '/trilhas-ilha-do-mel', label: 'todas as trilhas' },
  bar: { to: '/bares-ilha-do-mel', label: 'todos os bares' },
};

// Para passeios, o CTA depende do tipo: náuticos vão pra sub-pilar de barco; demais, pro departamento
function pilarNavFor(cat: string, entity: any): { to: string; label: string } | undefined {
  if (cat === 'passeio') {
    return entity?.type === 'nautico'
      ? { to: '/passeios-de-barco-ilha-do-mel', label: 'todos os passeios de barco' }
      : { to: '/o-que-fazer-ilha-do-mel', label: 'tudo o que fazer' };
  }
  return PILAR_NAV[cat];
}

function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-warm-gray mb-6 flex-wrap font-body">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-sand-300">/</span>}
          {item.to ? (
            <Link to={item.to} className="hover:text-teal-deep transition-colors">
              {item.label}
            </Link>
          ) : (
            <span className="text-warm-dark font-medium">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

function HighlightSection({
  title,
  items,
  icon,
}: {
  title: string;
  items: string[];
  icon: string;
}) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-8">
      <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
        {icon} {title.toUpperCase()}
      </h3>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 font-body text-warm-gray">
            <span className="text-teal-deep mt-1 flex-shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SecaoImage {
  src: string;
  alt?: string;
  credit?: string;
  creditUrl?: string;
}

function ProseSection({
  title,
  text,
  id,
  image,
}: {
  title: string;
  text?: string;
  id?: string;
  image?: SecaoImage;
}) {
  if (!text) return null;
  return (
    <div
      id={id}
      className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
    >
      <h2 className="font-display text-xl text-warm-dark mb-4 tracking-wide">{title}</h2>
      {image && (
        <figure className="mb-4 rounded-xl overflow-hidden">
          <img
            src={image.src}
            alt={image.alt || title}
            loading="lazy"
            decoding="async"
            className="w-full h-56 sm:h-72 object-cover"
          />
          {image.credit && (
            <figcaption className="font-body text-xs text-warm-gray mt-1.5">
              Foto:{' '}
              {image.creditUrl ? (
                <a
                  href={image.creditUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-deep hover:underline"
                >
                  {image.credit}
                </a>
              ) : (
                image.credit
              )}
            </figcaption>
          )}
        </figure>
      )}
      <RichText text={text} />
    </div>
  );
}

function InfoTag({
  label,
  value,
  color = 'teal',
}: {
  label: string;
  value: string;
  color?: string;
}) {
  const colorMap: Record<string, string> = {
    teal: 'bg-teal-light/10 text-teal-deep border-teal-light/20',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    coral: 'bg-coral/10 text-coral border-coral/20',
  };
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-body ${colorMap[color] || colorMap.teal}`}
    >
      <span className="text-warm-gray">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export function EntityDetailPage() {
  const { category, handle } = useParams<{ category: string; handle: string }>();
  const { lang } = useI18n();
  const localize = useLocalizedPath();
  const { t } = useTranslation();
  const { trackEntityView, trackOutbound } = useAnalytics();
  const googlePhotos = useGooglePhotos(handle);

  const categoryMap: Record<string, string> = {
    praia: 'praia',
    praias: 'praia',
    trilha: 'trilha',
    trilhas: 'trilha',
    atracao: 'atracao',
    atracoes: 'atracao',
    'ponto-turistico': 'atracao',
    'pontos-turisticos': 'atracao',
    passeio: 'passeio',
    passeios: 'passeio',
    pousada: 'pousada',
    pousadas: 'pousada',
    restaurante: 'restaurante',
    restaurantes: 'restaurante',
    bar: 'bar',
    bares: 'bar',
    morro: 'morro',
    morros: 'morro',
    vila: 'vila',
    vilas: 'vila',
    ilha: 'ilha',
    ilhas: 'ilha',
    trapiche: 'trapiche',
    trapiches: 'trapiche',
  };

  const cat = categoryMap[category || ''];
  if (!cat || !handle)
    return <NotFoundPage />;

  const entity = getByHandle(cat, handle, lang);

  useEffect(() => {
    if (entity) {
      trackEntityView({
        entityType: cat,
        entityName: entity.title,
        entityId: entity.handle,
        category: entity.category,
        vilarejo: entity.vilarejoName,
        partner: !!entity.provider?.commission,
      });
    }
  }, [entity, cat, trackEntityView]);

  if (!entity || entity.status !== 'published')
    return <NotFoundPage />;

  const e = entity;
  const isPartner = !!e.provider?.commission;
  const provider = e.provider;
  // Super página de pousada (piloto): usa o layout rico (PousadaWidgets) e oculta os blocos básicos duplicados
  const superPousada = cat === 'pousada' && !!e.superPage;
  // Super página de restaurante: layout rico (RestauranteWidgets), mesma lógica de ocultação
  const superRestaurante = cat === 'restaurante' && !!e.superPage;
  const superEntity = superPousada || superRestaurante;

  // Automação de imagens no artigo: distribui as fotos do Google (mesma fonte da
  // galeria) nas seções sem imagem curada. A foto 0 fica para a galeria/hero.
  const secoesComImagem =
    cat === 'praia' && e.secoes
      ? (() => {
          let gp = 1;
          return e.secoes.map((s: any) => {
            if (s.image) return s;
            const photo = googlePhotos[gp];
            if (!photo) return s;
            gp++;
            return {
              ...s,
              image: { src: photo.file, alt: s.title, credit: photo.attribution || undefined },
            };
          });
        })()
      : e.secoes || [];

  const heroImage = e.image || '/images/hero-bg.jpg';

  const relatedPraias = getRelated('praia', entity, 2, lang);
  const relatedTrilhas = getRelated('trilha', entity, 2, lang);
  const relatedAtracoes = getRelated('atracao', entity, 2, lang);
  const allRelated = [...relatedPraias, ...relatedTrilhas, ...relatedAtracoes].slice(0, 4);
  // Pousadas próximas e do mesmo vilarejo — "outras hospedagens" do hub de pousada
  const relatedPousadas = cat === 'pousada' ? getRelated('pousada', entity, 3, lang) : [];
  const relatedRestaurantes = cat === 'restaurante' ? getRelated('restaurante', entity, 3, lang) : [];
  // Praias vizinhas para a tabela comparativa (só praias, até 3)
  const praiasVizinhas = cat === 'praia' ? getRelated('praia', entity, 3, lang) : [];
  // Posts do cluster desta praia — síncrono (renderiza no SSR/prerender). Relacionados ficam DENTRO do hub.
  const clusterPosts = cat === 'praia' && handle ? getPostsMetaByHub(handle, lang) : [];

  return (
    <>
      <EntityDetailSEO
        title={e.title}
        description={e.description.substring(0, 160)}
        category={category || ''}
        image={heroImage}
        entity={e}
      />
      <div>
        <Container className="py-8">
          <Breadcrumb
            items={[
              { label: t('entity.breadcrumbHome'), to: localize('/') },
              {
                label:
                  cat === 'atracao'
                    ? 'Pontos turísticos'
                    : (category || '').charAt(0).toUpperCase() + (category || '').slice(1),
                // O nível de categoria aponta para a página pilar
                // (a listagem antiga redireciona para ela via 301)
                to: localize(
                  cat === 'praia'
                    ? '/praias-ilha-do-mel'
                    : cat === 'pousada'
                      ? '/pousadas-ilha-do-mel'
                      : cat === 'restaurante'
                        ? '/restaurantes-ilha-do-mel'
                        : cat === 'atracao'
                          ? '/pontos-turisticos-ilha-do-mel'
                          : `/${category}`
                ),
              },
              { label: e.title },
            ]}
          />

          <div className="mb-8">
            {isPartner && (
              <span className="inline-flex items-center gap-1 bg-coral/10 text-coral font-body text-xs font-bold px-3 py-1 rounded-full border border-coral/20 mb-3">
                {t('entity.partnerBadge')}
              </span>
            )}
            <h1 className="font-display text-4xl sm:text-5xl text-warm-dark mb-2">
              {e.h1 || (superEntity ? e.title : e.title.toUpperCase())}
            </h1>
            <p className="font-editorial text-xl text-warm-gray">{e.heroTagline || e.subtitle}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {e.vilarejoName && <InfoTag label={t('entity.labelLocal')} value={e.vilarejoName} />}
              {e.difficulty && (
                <InfoTag label={t('entity.labelDifficulty')} value={e.difficulty} color="amber" />
              )}
              {e.duration && (
                <InfoTag label={t('entity.labelDuration')} value={e.duration} color="emerald" />
              )}
              {e.price && !e.price.amount && (
                <InfoTag
                  label={t('entity.labelPrice')}
                  value={
                    typeof e.price === 'string'
                      ? e.price
                      : e.price.unit === 'gratuito'
                        ? 'Gratuito'
                        : 'Sob consulta'
                  }
                  color="coral"
                />
              )}
            </div>
          </div>

          <EntityGallery handle={handle} title={e.title} fallbackImage={heroImage} />

          {typeof e.rating === 'number' && e.rating > 0 && (
            <div className="flex justify-end mb-4">
              <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg border border-sand-200">
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="font-body font-bold text-warm-dark">{e.rating}</span>
                {typeof e.reviewCount === 'number' && (
                  <span className="font-body text-sm text-warm-gray">({e.reviewCount})</span>
                )}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {cat === 'pousada' && e.superPage && <PousadaProvaSocial entity={e} />}
              {cat === 'restaurante' && e.superPage && <RestauranteProvaSocial entity={e} />}
              <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <h2 className="font-display text-xl text-warm-dark mb-4 tracking-wide">
                  {cat === 'praia' || superPousada
                    ? `Sobre a ${e.title}`
                    : superRestaurante
                      ? `Sobre o ${e.title}`
                      : t('entity.sectionAbout')}
                </h2>
                {superEntity && e.descricaoLonga?.length ? (
                  e.descricaoLonga.map((p: string, i: number) => (
                    <RichText key={i} text={p} className={i > 0 ? 'mt-3' : ''} />
                  ))
                ) : (
                  <RichText text={e.description} />
                )}
              </div>

              {cat === 'pousada' && e.superPage && <PousadaSuperSections entity={e} />}
              {cat === 'restaurante' && e.superPage && <RestauranteSuperSections entity={e} />}

              {cat === 'praia' && (
                <>
                  <PraiaQuickFacts entity={e} />
                  <PraiaTideSplit entity={e} />

                  {e.comoChegar && (
                    <div
                      id="como-chegar"
                      className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                    >
                      <h2 className="font-display text-xl text-warm-dark mb-4 tracking-wide">
                        Como chegar e acesso pela trilha
                      </h2>
                      <PraiaArrivalTimeline entity={e} />
                      <RichText text={e.comoChegar} className="mt-5" />
                    </div>
                  )}

                  {secoesComImagem.map(
                    (s: { id: string; title: string; text: string; image?: SecaoImage }) => (
                      <ProseSection key={s.id} id={s.id} title={s.title} text={s.text} image={s.image} />
                    )
                  )}
                </>
              )}

              {/* Ponto turístico: layout visual (ficha + destaques/dicas em cards + mapa + FAQ) */}
              {cat === 'atracao' && <AtracaoVisualSections entity={e} />}
              {cat === 'bar' && <BarVisualSections entity={e} />}
              {!superEntity && cat !== 'atracao' && cat !== 'bar' && (
                <>
                  <HighlightSection
                    title={t('entity.sectionHighlights')}
                    items={e.highlights || []}
                    icon="✨"
                  />
                  <HighlightSection title={t('entity.sectionTips')} items={e.tips || []} icon="💡" />
                </>
              )}
              {cat === 'praia' && e.oQueLevar?.length > 0 && (
                <div id="o-que-levar">
                  <h2 className="font-display text-xl text-warm-dark mb-4 tracking-wide">
                    O que levar na mochila
                  </h2>
                  <PraiaPackingGrid items={e.oQueLevar} />
                </div>
              )}
              {cat === 'praia' && praiasVizinhas.length > 0 && (
                <PraiaComparativa entity={e} relacionadas={praiasVizinhas} />
              )}
              {cat === 'praia' && <PraiaTideCTA />}

              {!superEntity && <EntityWidgets entity={e} />}

              {cat === 'passeio' && e.itinerary && (
                <div className="mb-8">
                  <h3 className="flex items-center gap-2 font-display text-lg mb-4 text-warm-dark tracking-wide">
                    {t('entity.sectionItinerary')}
                  </h3>
                  <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <div className="space-y-0">
                      {e.itinerary.map((item: any, i: number) => (
                        <div key={i} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="w-3 h-3 rounded-full bg-teal-deep flex-shrink-0" />
                            {i < e.itinerary.length - 1 && (
                              <div className="w-0.5 h-full bg-teal-light my-1" />
                            )}
                          </div>
                          <div className="pb-6">
                            <span className="font-body text-teal-deep font-mono text-sm">
                              {item.time}
                            </span>
                            <h4 className="font-body font-semibold text-warm-dark mt-0.5">
                              {item.activity}
                            </h4>
                            <p className="font-body text-sm text-warm-gray">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {cat === 'passeio' && (
                <>
                  {e.included?.length > 0 && (
                    <HighlightSection
                      title={t('entity.sectionIncluded')}
                      items={e.included}
                      icon="✅"
                    />
                  )}
                  {e.notIncluded?.length > 0 && (
                    <HighlightSection
                      title={t('entity.sectionNotIncluded')}
                      items={e.notIncluded}
                      icon="❌"
                    />
                  )}
                  {e.whatToBring?.length > 0 && (
                    <HighlightSection
                      title={t('entity.sectionWhatToBring')}
                      items={e.whatToBring}
                      icon="🎒"
                    />
                  )}
                </>
              )}

              {cat === 'pousada' && !superPousada && e.amenities?.length > 0 && (
                <div className="mb-8">
                  <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
                    {t('entity.sectionAmenities')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {e.amenities.map((a: string) => (
                      <span
                        key={a}
                        className="font-body text-sm bg-teal-light/10 text-teal-deep border border-teal-light/20 px-3 py-1 rounded-lg capitalize"
                      >
                        {a.replace(/-/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {cat === 'pousada' && !superPousada && e.roomTypes?.length > 0 && (
                <div className="mb-8">
                  <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
                    {t('entity.sectionRoomTypes')}
                  </h3>
                  <div className="space-y-3">
                    {e.roomTypes.map((room: any) => (
                      <div
                        key={room.name}
                        className="bg-white border border-sand-200 rounded-xl p-4 flex justify-between items-center shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                      >
                        <div>
                          <h4 className="font-body font-semibold text-warm-dark">{room.name}</h4>
                          <p className="font-body text-sm text-warm-gray">{room.description}</p>
                        </div>
                        <span className="font-display text-lg text-coral">R$ {room.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cat === 'restaurante' && !superRestaurante && e.specialties?.length > 0 && (
                <HighlightSection
                  title={t('entity.sectionSpecialties')}
                  items={e.specialties}
                  icon="🍽️"
                />
              )}
              {cat === 'restaurante' && !superRestaurante && e.openingHours && (
                <div className="mb-8">
                  <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
                    {t('entity.sectionOpeningHours')}
                  </h3>
                  <div className="bg-white border border-sand-200 rounded-xl p-5 space-y-1 font-body text-sm text-warm-gray shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    {e.openingHours.breakfast && (
                      <p>
                        <span className="font-medium">{t('entity.labelBreakfast')}</span>{' '}
                        {e.openingHours.breakfast}
                      </p>
                    )}
                    {e.openingHours.lunch && (
                      <p>
                        <span className="font-medium">{t('entity.labelLunch')}</span>{' '}
                        {e.openingHours.lunch}
                      </p>
                    )}
                    {e.openingHours.dinner && (
                      <p>
                        <span className="font-medium">{t('entity.labelDinner')}</span>{' '}
                        {e.openingHours.dinner}
                      </p>
                    )}
                    <p>
                      <span className="font-medium">{t('entity.labelDays')}</span>{' '}
                      {e.openingHours.days}
                    </p>
                  </div>
                </div>
              )}

              {cat === 'atracao' && e.history && (
                <div className="mb-8">
                  <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
                    {t('entity.sectionHistory')}
                  </h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-2 font-body text-sm text-warm-gray">
                    {Object.entries(e.history).map(([key, value]: [string, any]) => (
                      <p key={key}>
                        <span className="text-amber-700 font-medium capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                        </span>{' '}
                        {value}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {cat === 'atracao' && e.legend && (
                <div className="mb-8">
                  <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
                    📖 {e.legend.title.toUpperCase()}
                  </h3>
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-5 font-editorial text-warm-gray text-lg italic leading-relaxed">
                    "{e.legend.text}"
                  </div>
                </div>
              )}

              {(() => {
                const nav = pilarNavFor(cat, e);
                return nav ? <NavegacaoPilar to={localize(nav.to)} label={nav.label} /> : null;
              })()}
            </div>

            <div className="space-y-6">
              {e.price?.amount && (
                <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                  <div className="mb-4">
                    <span className="font-display text-3xl text-coral">R$ {e.price.amount}</span>
                    <span className="font-body text-warm-gray ml-1">/{e.price.unit}</span>
                    {e.price.notes && (
                      <p className="font-body text-xs text-warm-gray mt-1">{e.price.notes}</p>
                    )}
                  </div>
                  {provider && (
                    <WhatsAppButton
                      provider={provider}
                      source={`${category}-${handle}`}
                      label={t('entity.ctaBookWhatsApp')}
                      className="w-full justify-center"
                    />
                  )}
                  {e.cancellationPolicy && (
                    <p className="font-body text-xs text-warm-gray mt-3">
                      <span className="font-medium">{t('entity.labelPolicy')}</span>{' '}
                      {e.cancellationPolicy}
                    </p>
                  )}
                </div>
              )}

              {cat === 'pousada' && !superPousada && provider && (provider.phone || provider.bookingComUrl) && (
                <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                  <h3 className="font-display text-lg mb-3 text-warm-dark tracking-wide">
                    {provider.phone ? t('entity.sectionBookDirect') : t('entity.sectionStay')}
                  </h3>
                  <p className="font-body text-sm text-warm-gray mb-4">
                    {provider.phone ? t('entity.bookDirectDesc') : t('entity.bookingDesc')}
                  </p>
                  {provider.phone ? (
                    <WhatsAppButton
                      provider={provider}
                      source={`${category}-${handle}`}
                      label={t('entity.ctaBookWhatsApp')}
                      className="w-full justify-center"
                    />
                  ) : (
                    <a
                      href={provider.bookingComUrl}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      onClick={() => trackOutbound(provider.bookingComUrl, e.title, 'booking_pousada')}
                      className="flex w-full justify-center items-center gap-2 bg-coral hover:bg-coral-dark text-white font-body font-semibold rounded-xl px-6 py-3 transition-colors"
                    >
                      {t('entity.ctaBooking')}
                    </a>
                  )}
                  {(e.rating?.booking || e.rating?.tripAdvisor) && (
                    <div className="mt-4 pt-4 border-t border-sand-200 text-center">
                      <span className="font-display text-2xl text-amber-500">
                        {e.rating.booking || e.rating.tripAdvisor}
                      </span>
                      <span className="font-body text-sm text-warm-gray ml-1">
                        {e.rating.booking ? t('entity.onBooking') : t('entity.onTripAdvisor')}
                      </span>
                      {(e.reviewCount?.booking || e.reviewCount?.tripAdvisor) && (
                        <span className="font-body text-xs text-warm-gray ml-1">
                          {t('entity.reviewsCount').replace(
                            '{{count}}',
                            String(e.reviewCount.booking || e.reviewCount.tripAdvisor)
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {cat === 'restaurante' && isPartner && (
                <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                  <h3 className="font-display text-lg mb-3 text-warm-dark tracking-wide">
                    {t('entity.sectionReservationRecommended')}
                  </h3>
                  <p className="font-body text-sm text-warm-gray mb-4">
                    {t('entity.reservationDesc')}
                  </p>
                  <WhatsAppButton
                    provider={provider}
                    source={`${category}-${handle}`}
                    label={t('entity.ctaReserveTable')}
                    className="w-full justify-center"
                  />
                </div>
              )}

              {!superEntity && (
              <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <h3 className="font-display text-lg mb-4 text-warm-dark tracking-wide">
                  {t('entity.sectionInfo')}
                </h3>
                <div className="space-y-3 font-body text-sm">
                  {e.vilarejoName && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelVilarejo')}</span>
                      <span className="text-warm-dark font-medium">{e.vilarejoName}</span>
                    </div>
                  )}
                  {e.difficulty && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelDifficulty')}</span>
                      <span className="text-warm-dark font-medium">{e.difficulty}</span>
                    </div>
                  )}
                  {e.duration && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelDuration')}</span>
                      <span className="text-warm-dark font-medium">{e.duration}</span>
                    </div>
                  )}
                  {e.distance && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelDistance')}</span>
                      <span className="text-warm-dark font-medium">{e.distance}</span>
                    </div>
                  )}
                  {e.price && !e.price.amount && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelPrice')}</span>
                      <span className="text-warm-dark font-medium">
                        {typeof e.price === 'string'
                          ? e.price
                          : e.price.unit === 'gratuito'
                            ? 'Gratuito'
                            : 'Sob consulta'}
                      </span>
                    </div>
                  )}
                  {e.groupSize && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelGroup')}</span>
                      <span className="text-warm-dark font-medium">
                        {t('entity.peopleCount')
                          .replace('{{min}}', String(e.groupSize.min))
                          .replace('{{max}}', String(e.groupSize.max))}
                      </span>
                    </div>
                  )}
                  {e.meetingPoint && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelMeetingPoint')}</span>
                      <span className="text-warm-dark font-medium text-right">
                        {e.meetingPoint}
                      </span>
                    </div>
                  )}
                  {e.bestTime && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelBestTime')}</span>
                      <span className="text-warm-dark font-medium">{e.bestTime}</span>
                    </div>
                  )}
                  {e.bestSeason && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelBestSeason')}</span>
                      <span className="text-warm-dark font-medium">{e.bestSeason}</span>
                    </div>
                  )}
                  {e.bestTide && (
                    <div className="flex justify-between">
                      <span className="text-warm-gray">{t('entity.labelBestTide')}</span>
                      <span className="text-warm-dark font-medium">{e.bestTide}</span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {cat === 'praia' && e.facilities?.length > 0 && (
                <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                  <h3 className="font-display text-lg mb-3 text-warm-dark tracking-wide">
                    {t('entity.sectionInfrastructure')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {e.facilities.map((f: string) => (
                      <span
                        key={f}
                        className="font-body text-sm bg-teal-light/10 text-teal-deep border border-teal-light/20 px-3 py-1 rounded-lg capitalize"
                      >
                        {f.replace(/-/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {cat === 'praia' && e.faq?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-sand-200 max-w-3xl" id="faq">
              <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-8 tracking-wide">
                Perguntas frequentes
              </h2>
              <div className="space-y-3">
                {e.faq.map((item: any, i: number) => (
                  <details
                    key={i}
                    className="group bg-white border border-sand-200 rounded-lg overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                      <span className="font-display text-base text-warm-dark pr-4">
                        {item.pergunta}
                      </span>
                      <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">
                        ▼
                      </span>
                    </summary>
                    <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">
                      {item.resposta}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}

          <AuthorBox author={defaultAuthor} />

          {e.nearbyTrapiches?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-sand-200">
              <div className="text-center mb-8">
                <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
                  {t('entity.nearbyOverline')}
                </span>
                <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mt-1 tracking-wide">
                  {t('entity.nearbyTitle')}
                </h2>
                <p className="font-editorial text-lg text-warm-gray mt-2">
                  {t('entity.nearbySubtitle')}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {e.nearbyTrapiches.map((trapicheHandle: string) => {
                  const trapiche = getByHandle('trapiche', trapicheHandle, lang);
                  if (!trapiche) return null;
                  return (
                    <Link
                      key={trapicheHandle}
                      to={localize(`/trapiche/${trapicheHandle}`)}
                      className="group block bg-white border border-coral/20 hover:border-coral/40 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={trapiche.image}
                          alt={trapiche.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="p-4">
                        <span className="inline-block font-body text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-coral/10 text-coral border-coral/20">
                          {t('entity.labelTrapiche')}
                        </span>
                        <h3 className="font-display text-warm-dark group-hover:text-coral transition-colors mt-2 tracking-wide">
                          {trapiche.title.toUpperCase()}
                        </h3>
                        <p className="font-body text-sm text-warm-gray mt-1 line-clamp-2">
                          {trapiche.subtitle}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {cat === 'praia' && clusterPosts.length > 0 && (
            <div className="mt-12 pt-8 border-t border-sand-200">
              <div className="text-center mb-8">
                <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
                  Guias da {e.title}
                </span>
                <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mt-1 tracking-wide">
                  Tudo sobre a {e.title}
                </h2>
                <p className="font-editorial text-lg text-warm-gray mt-2">
                  Dúvidas reais de quem visita, respondidas em detalhe.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {clusterPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={localize(`/blog/${post.slug}`)}
                    className="group block bg-white border border-sand-200 hover:border-teal-light/40 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-display text-warm-dark group-hover:text-teal-deep transition-colors tracking-wide line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="font-body text-sm text-warm-gray mt-1 line-clamp-2">{post.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {cat === 'pousada' && relatedPousadas.length > 0 && (
            <div className="mt-12 pt-8 border-t border-sand-200">
              <div className="text-center mb-8">
                <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
                  Onde ficar por perto
                </span>
                <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mt-1 tracking-wide">
                  Outras hospedagens em {e.vilarejoName}
                </h2>
                <p className="font-editorial text-lg text-warm-gray mt-2">
                  Pousadas próximas, no mesmo canto da ilha.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedPousadas.map((rel: any) => (
                  <Link
                    key={rel.handle}
                    to={localize(`/pousada/${rel.handle}`)}
                    className="group block bg-white border border-sand-200 hover:border-teal-light/40 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={(googleThumbs as Record<string, string>)[rel.handle] || rel.image || '/images/hero-bg.jpg'}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-display text-warm-dark group-hover:text-teal-deep transition-colors tracking-wide">
                        {rel.title}
                      </h3>
                      <p className="font-body text-sm text-warm-gray mt-1 line-clamp-2">{rel.subtitle}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {allRelated.length > 0 && clusterPosts.length === 0 && cat !== 'pousada' && (
            <div className="mt-12 pt-8 border-t border-sand-200">
              <div className="text-center mb-8">
                <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
                  {t('entity.relatedOverline')}
                </span>
                <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mt-1 tracking-wide">
                  {t('entity.relatedTitle')}
                </h2>
                <p className="font-editorial text-lg text-warm-gray mt-2">
                  {t('entity.relatedSubtitle')}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {allRelated.map((rel: any) => {
                  const relImage = (googleThumbs as Record<string, string>)[rel.handle] || rel.image || '/images/hero-bg.jpg';
                  const categoryColors: Record<string, string> = {
                    praia: 'bg-ocean/10 text-ocean border-ocean/20',
                    trilha: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    atracao: 'bg-amber-50 text-amber-700 border-amber-200',
                    passeio: 'bg-violet-50 text-violet-700 border-violet-200',
                    pousada: 'bg-rose-50 text-rose-700 border-rose-200',
                    restaurante: 'bg-orange-50 text-orange-700 border-orange-200',
                    morro: 'bg-teal-50 text-teal-700 border-teal-200',
                    vila: 'bg-sand-100 text-warm-dark border-sand-300',
                    ilha: 'bg-sky-50 text-sky-700 border-sky-200',
                    trapiche: 'bg-coral/10 text-coral border-coral/20',
                  };
                  return (
                    <Link
                      key={rel.handle}
                      to={localize(
                        `/${rel.category === 'praia' ? 'praias' : rel.category === 'atracao' ? 'ponto-turistico' : rel.category}/${rel.handle}`
                      )}
                      className="group block bg-white border border-sand-200 hover:border-teal-light/40 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={relImage}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="p-4">
                        <span
                          className={`inline-block font-body text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${categoryColors[rel.category] || categoryColors.praia}`}
                        >
                          {rel.category}
                        </span>
                        <h3 className="font-display text-warm-dark group-hover:text-teal-deep transition-colors mt-2 tracking-wide">
                          {rel.title.toUpperCase()}
                        </h3>
                        <p className="font-body text-sm text-warm-gray mt-1 line-clamp-2">
                          {rel.subtitle}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </Container>
      </div>
    </>
  );
}
